import { KeycloakTokenParsed } from 'keycloak-js';
import { effect, inject, Injectable, signal } from '@angular/core';
import { KeycloakEvent, KeycloakService } from './keycloak.service';

@Injectable({
  providedIn: 'root',
})
export class AuthSsoService {
  private _tokenParsed: KeycloakTokenParsed | undefined = {};

  private keycloakService = inject(KeycloakService);

  fdGwToken: string | undefined = undefined;

  readonly fdGWTokenSignal = signal<string>('');
  readonly isLoggedIn = signal<boolean>(false);

  constructor() {
    // i mean this likely needs to be refactored whole setting on token logic and where to do it
    this.fdGwToken = this.keycloakService.getToken();
    if (this.fdGwToken) this.fdGWTokenSignal.set(this.fdGwToken);

    window.addEventListener('navbarAccountLogOut', () => {
      console.log('navbar account logout');
      this.logoutFromNavbar();
    });

    effect(() => {
      return this.handleKeycloakEvent(this.keycloakService.keyCloakEventSignal());
    });
  }

  handleKeycloakEvent(event: KeycloakEvent | null): void {
    if (event)
      switch (event.type) {
        case 'onReady':
          console.log('[authService] Keycloak event: OnReady, Authenticated?:', event.authenticated);
          break;

        case 'onAuthSuccess':
          console.log('[authService] Keycloak event onAuthSuccess');
          this.onSuccessfulKeycloakLogin();
          break;

        case 'onAuthError':
          console.error('[authService] Keycloak event: OnAuthError', event.error);
          this.onKeycloakAuthError(event);
          break;

        case 'onAuthRefreshSuccess':
          console.log('[authService] Keycloak event.type: OnAuthRefreshSuccess');
          break;

        case 'onAuthRefreshError':
          console.error('[authService] Keycloak event: OnAuthRefreshError', event);
          break;

        case 'onAuthLogout':
          console.log('[authService] Keycloak event: OnAuthLogout', event);
          this.logout('KeycloakEventType.OnAuthLogout').then(() => {
            console.log('[authService] logout completed');
          });
          break;

        case 'onTokenExpired':
          console.warn('Token expired. Attempting to refresh...');
          this.refreshToken().then(() => {
            console.log('[authService] token refreshed on expiry.');
          });
          break;

        default:
          console.log('Unhandled Keycloak event:', event);
      }
  }

  private onKeycloakAuthError(event: KeycloakEvent): void {
    console.log('[authService] Keycloak auth error');
  }

  /**
   * Refresh the Keycloak token and create a new token
   *
   * @returns boolean flag indicating if refreshToken and token creation is completed successfully or not
   */
  public async refreshToken(): Promise<boolean> {
    let tokenRefreshed = false;

    try {
      tokenRefreshed = await this.keycloakService.getKeyCloakInstance().updateToken(-1); // -1: to force refresh the token
      this._tokenParsed = this.keycloakService.getParsedToken();
    } catch (error) {
      await this.logout(`Error in refreshing the token: ${error}`);
      return false;
    }

    if (!tokenRefreshed) {
      return false;
    }

    this.fdGwToken = await this.keycloakService.getToken();
    if (this.fdGwToken) {
      return true;
    }

    return false;
  }

  dispatchNavbarTokens() {
    // dispatch navbar event when token is refreshed
    document.dispatchEvent(
      new CustomEvent('navbar_RefreshToken', {
        detail: this.fdGwToken,
      }),
    );

    document.dispatchEvent(
      new CustomEvent('navbar_UpdateToken', {
        detail: this.fdGwToken,
      }),
    );
  }

  /**
   * boolean flag indicating if Keycloak token is expired or not
   *
   * @returns boolean flag indicating if Keycloak token is expired or not
   * @description If keycloak is not initialized then this function will return false!
   */
  public isKeycloakTokenExpired(): boolean {
    if (this._tokenParsed == null) {
      return false;
    }
    try {
      const tokenExpiryTimeEpoch = this._tokenParsed.exp || -1;
      const expireInSeconds = Math.round(tokenExpiryTimeEpoch - new Date().getTime() / 1000);
      return expireInSeconds <= 0;
    } catch (error) {
      console.error('[authService] failed to get token expiry.', error);
      return false;
    }
  }

  /**
   * logout the user
   *
   * @param reason - logout reason
   */
  public async logout(reason: string, localhost = false): Promise<void> {
    console.log('[authService] logout - reason: ' + reason);

    this.isLoggedIn.set(false);
    this.fdGwToken = undefined;
    this.fdGWTokenSignal.set('');

    window.sessionStorage.clear();

    await this.keycloakService.logout();
  }

  private logoutFromNavbar() {
    const local = window.location.href.includes('localhost');
    this.logout('UserLoggedout', local);
  }

  /**
   * method invoked after successful keycloak login
   */
  private async onSuccessfulKeycloakLogin(): Promise<void> {
    this._tokenParsed = this.keycloakService.getParsedToken();

    const token = this.keycloakService.getToken();

    if (token && this._tokenParsed) {
      this.fdGwToken = token;
      this.isLoggedIn.set(true);
      this.fdGWTokenSignal.set(token);

      this.dispatchNavbarTokens();
    }
  }
}
